import { ENV, PLATFORM_FLAG, raise, } from "@bracket/graphql/helpers";
import { getRequestShape, } from "@bracket/graphql/requests";
import type { Requests, } from "@bracket/graphql/requests";
import type { EthereumAddress, } from "@bracket/types";
type QueryIdentifier = keyof Requests["query"];
type QueryArguments<Q extends QueryIdentifier> = keyof Requests["query"][Q]["arguments"];


export declare type QueryOptions<N extends keyof QueryIdentifier> = {
	variables?: keyof QueryArguments<N>;
	endpoint?: `http${string}` | "STAGING" | "PRODUCTION" | "LOCAL";
};

export declare type BrowserClientOptions = {
	endpoints?: Record<"DEFAULT" | "STAGING" | "PRODUCTION" | "LOCAL", `http${string}`>;
	wallet?: EthereumAddress;
};



export class BrowserClient {

	endpoints: Map<"DEFAULT" | "STAGING" | "PRODUCTION" | "LOCAL" | string, `http${string}`> = new Map();
	headers: { wallet?: EthereumAddress; } = {};

	constructor( { endpoints, wallet, }: BrowserClientOptions ) {

		if(!endpoints) {
			throw new Error("endpoints are required");
		}

		this.endpoints = new Map(Object.entries(endpoints));
		this.headers = Object.assign({}, wallet ? { wallet, } : {});

	}

	public query = async <N extends QueryIdentifier>( query: N, options:QueryOptions<N> = {} ): Promise<typeof requests.query[N]["response"]["data"]> => {

		const { variables, endpoint, } = options;
		const requests = await getRequestShape();
		const { arguments: variablesShape, path, } = requests.query[query];

		const url = this._electEndpoint();
		if(!url) {
			throw new Error(`unknown endpoint ${endpoint} for query ${query}`);
		}

		if(Object.keys(variables ?? {}).length !== Object.keys(variablesShape ?? {}).length) {
			const missingVariables = Object.keys(variablesShape ?? {}).filter(key => !(key in (variables ?? {})));
			const misspelledVariables = Object.keys(variables ?? {}).filter(key => !(key in (variablesShape ?? {})));
			console.log(`
				invalid variables provided for query ${query}
				missing variables: ${missingVariables.join(", ")}
				misspelled variables: ${misspelledVariables.join(", ")}
			`)
			throw new Error(`invalid variables provided for query ${query}`);
		}

		const response = await fetch(url, {
			method: requests.query[query].method,
			headers: {
				"Content-Type": "application/json",
				...this.headers,
				...requests.query[query].headers,
			},
			body: JSON.stringify({
				query: requests.query[query].query,
				variables: options.variables,
			}),
		});

		if(response.ok) {
			const { data, } = await response.json();
			try {
				const result = path.split(".").reduce((acc, key) => acc[key], data);
				return result;
			}
			catch(error) {
				console.error(`failed to parse response for query ${query}: ${error}`);
				throw new Error(`failed to parse response for query ${query}`);
			}
		}
		else {
			throw new Error(`failed to fetch query ${query}: ${response.statusText}`);
		}
	}

	private _electEndpoint = ( ): `http${string}` => {

		if(ENV === "PRODUCTION") {
			return this.endpoints.get("PRODUCTION") as `http${string}`;
		}
		if(PLATFORM_FLAG === "BROWSER") {
			const params = new URLSearchParams(globalThis.location.search);
			const paramed = params.get("gql");
			if(paramed === "local") {
				return this.endpoints.get("LOCAL") as `http${string}`;
			}
			if(paramed && this.endpoints.has(paramed.toUpperCase())) {
				return this.endpoints.get(paramed) as `http${string}`;
			}
		}
		return this.endpoints.get("DEFAULT") as `http${string}`;
	}

	public static create = ( options: BrowserClientOptions = { } ): BrowserClient => {

		const { VITE_GQL_URL: DEFAULT, VITE_DEV_GQL_URL: STAGING, VITE_PROD_GQL_URL: PRODUCTION, } = typeof window !== "undefined" ? import.meta?.env ?? {} : process.env ?? {};

		const {
			endpoints = {
				DEFAULT: "https://staging.bracket.fi/api/graphql",
				STAGING: "https://staging.bracket.fi/api/graphql",
				PRODUCTION: "https://app.bracket.fi/api/graphql",
				LOCAL: "http://localhost:3000/graphql",
			},
			wallet,
		} = options;

		return new BrowserClient({ endpoints, wallet, });
	}

}




